import { Clock, Bot, ArrowRightLeft } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Textarea from '@/components/ui/Textarea';
import type { StatusHistoryEvent, FulfillmentEvent } from './types';

interface UnifiedTimelineProps {
  statusHistory: StatusHistoryEvent[];
  fulfillmentEvents: FulfillmentEvent[];
  newNote: string;
  isAddingNote: boolean;
  onNoteChange: (value: string) => void;
  onAddNote: (e: React.FormEvent) => void;
}

interface TimelineEntry {
  id: string;
  type: 'status' | 'fulfillment';
  created_at: string;
  data: StatusHistoryEvent | FulfillmentEvent;
}

const renderNoteWithMentions = (text: string) => {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} className="text-blue-600 font-medium">{part}</span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const getFulfillmentStatusVariant = (status: string): 'green' | 'yellow' | 'red' | 'blue' | 'gray' => {
  switch (status) {
    case 'completed': case 'delivered': return 'green';
    case 'pending': case 'processing': return 'yellow';
    case 'failed': case 'error': return 'red';
    case 'in_progress': case 'shipped': return 'blue';
    default: return 'gray';
  }
};

export default function UnifiedTimeline({
  statusHistory,
  fulfillmentEvents,
  newNote,
  isAddingNote,
  onNoteChange,
  onAddNote,
}: UnifiedTimelineProps) {
  // Merge and sort entries newest-first
  const entries: TimelineEntry[] = [
    ...statusHistory.map((e) => ({
      id: e.id,
      type: 'status' as const,
      created_at: e.created_at,
      data: e,
    })),
    ...(fulfillmentEvents || []).map((e) => ({
      id: e.id,
      type: 'fulfillment' as const,
      created_at: e.created_at,
      data: e,
    })),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return (
    <Card title="Timeline" padding="md">
      {/* Comment input */}
      <form onSubmit={onAddNote} className="mb-4">
        <Textarea
          placeholder="Add a note... Use @name to mention someone"
          value={newNote}
          onChange={(e) => onNoteChange(e.target.value)}
          className="mb-2"
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!newNote.trim()}
          isLoading={isAddingNote}
        >
          Add Note
        </Button>
      </form>

      {/* Timeline entries */}
      <div className="space-y-4 border-t border-stone-100 pt-4">
        {entries.map((entry) => {
          if (entry.type === 'status') {
            const event = entry.data as StatusHistoryEvent;
            return (
              <div key={entry.id} className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Clock className="h-4 w-4 text-stone-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-700">
                    {event.description || event.status || 'Status update'}
                  </p>
                  {event.notes && (
                    <p className="text-sm text-stone-600 mt-1">
                      {renderNoteWithMentions(event.notes)}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    {event.created_by_name && (
                      <span className="text-xs text-stone-500">{event.created_by_name}</span>
                    )}
                    <span className="text-xs text-stone-400">{timeAgo(event.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          }

          const event = entry.data as FulfillmentEvent;
          return (
            <div key={entry.id} className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {event.source === 'agent' ? (
                  <Bot className="h-4 w-4 text-purple-400" />
                ) : (
                  <ArrowRightLeft className="h-4 w-4 text-blue-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={event.source === 'agent' ? 'blue' : 'gray'}>
                    {event.source === 'agent' ? 'AI Agent' : 'Supplier'}
                  </Badge>
                  <span className="text-sm text-stone-700">{event.type.replace(/_/g, ' ')}</span>
                  <Badge variant={getFulfillmentStatusVariant(event.status)}>
                    {event.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
                {event.detail && (
                  <p className="text-sm text-stone-600 mt-1">{event.detail}</p>
                )}
                <span className="text-xs text-stone-400 mt-1 block">{timeAgo(event.created_at)}</span>
              </div>
            </div>
          );
        })}

        {entries.length === 0 && (
          <p className="text-sm text-stone-400 text-center py-4">No timeline events yet.</p>
        )}
      </div>
    </Card>
  );
}
